import React, { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import './RothConversionSteps.scss';
import ProgressStep from '../../ProgressStep/ProgressStep';
import CheckBox from 'src/components/Shared/Checkbox/Checkbox';
import AppTitle from '../../Shared/AppTitle/AppTitle';
import { moduleActions } from '../../../redux/modules';
import {
  isCustomerWebViewEnabled,
  isEmbeddedInIFrame,
  isSalesforceRep,
} from '../../../redux/modules/authentication/authentication.module.selectors';
import {
  CANCEL_TRADE_CONFIRMATION_MODAL,
  FEDERAL_INCOME_TAX_WARNING,
  IRA_WITHHOLDING_WARNING_MODAL,
  REQUIRED_MINIMUM_DISTRIBUTION_MODAL,
} from '../../../redux/modules/modals/modals.module';
import { scrollElementIntoFullView } from '../../../utils/dom-utils';

// Steps
import RothConversionFromForm from '../step1/RothConversionFromForm/RothConversionFromForm';
import RothConversionSummary from '../common/RothConversionSummary/RothConversionSummary';
import RothConversionTaxWithholdingForm from '../step2/RothConversionTaxWithholding/RothConversionTaxWithholdingForm';
import RothBeneficiaryFrom from '../step3/BeneficiaryForm/BeneficiaryForm';
import RothConversionTermsAndConditions from '../step4/RothConversionsTermsAndConditions/RothConversionTermsAndConditions';
import RothConversionReview from '../step5/RothConversionReview/RothConversionReview';
// Redux related
import { getProgressCurrentStep } from 'src/redux/modules/progress/selectors';
import {
  getMinMaxFromFundsCheck,
  getRothConversionTaxOption,
  getRothConversionTaxPercentage,
  getRothConversionTermsConditions,
  getRothConversionTaxPercentageCheck,
  getValidateUserRothBeneficiaries,
} from '../../../redux/modules/rothConversionTransaction/rothConversionTransaction.selectors';
import { getRothConversionExecuteState } from '../../../redux/modules/executeRothConversion/executeRothConversion.selectors';
import { getMandatoryWithholdingStates } from '../../../redux/modules/stateWithholding/selectors';
import { ROTH_CONVERSION_TAX_OPTIONS } from '../../../settings/settings';
import { getRothIraFunds } from '../../../redux/modules/funds/UserFunds/userFunds.selectors';
import { bindActionCreators, compose } from 'redux';
import { getUserState } from '../../../redux/modules/user/PersonalInformation/personalInformation.selectors';
import If from '../../Shared/If/If';
import { getIsRMDEligible } from '../../../redux/modules/RMD/RMDSetting.selectors';
import { getRothConversionTradeDates } from '../../../redux/modules/executeRothConversion/executeRothConversion.module';
import { createRothConversionTransaction } from '../../../redux/modules/executeRothConversion/rothConversion.helper';
// Contains internal state and callbacks to track fundlists used in steps
const useSelectedFunds = () => {
  // Step Tracking
  const currentStep = useSelector(getProgressCurrentStep);
  //redux
  const dispatch = useDispatch();
  const {
    selectConversionFund,
    deselectConversionFund,
    updateConversionFund,
    batchSelectConversionFund,
  } = moduleActions;

  // FUND INPUT CALLBACK
  const handleFundAllocations = useCallback(
    (fund, value) => {
      dispatch(
        updateConversionFund(fund.agreementID, value.customProps, currentStep)
      );
    },
    [currentStep, dispatch, updateConversionFund]
  );

  // FUND SELECTION CALLBACK
  const handleSelectFund = useCallback(
    (agreementID, fund) => {
      let step;
      if (currentStep === 1) {
        step = 1;
      } else if (currentStep === 2) {
        step = 2;
      }
      if (fund.selected) {
        // handleFundAllocations(fund, { type: 'CONVERT_ALL' }, step);
        // if selected dispatch action
        dispatch(deselectConversionFund(agreementID, step));
      } else {
        // if selected dispatch action
        dispatch(selectConversionFund(agreementID, { ...fund }, step));
      }
    },
    [dispatch, selectConversionFund, deselectConversionFund, currentStep]
  );

  const batchSelectFunds = useCallback(
    (funds, step) => {
      const fundsMap = funds.reduce(
        (acc, f) => ({
          ...acc,
          [f.agreementID]: {
            ...f,
            grossAmount: f.grossAmount,
            grossAmountDollars: f.grossAmountDollars,
            grossUnit: f.grossUnit,
            selected: true,
            newFund: f.newFund,
          },
        }),
        {}
      );
      dispatch(batchSelectConversionFund(fundsMap, step));
    },
    [batchSelectConversionFund, dispatch]
  );

  return {
    handleSelectFund,
    handleFundAllocations,
    batchSelectFunds,
  };
};

/**
 * Roth Conversion Steps {Component}
 * Controls what to show for each step of the flow
 */
const isNotCustViewAndSalesForceRep = (
  customerWebViewEnabled,
  isSalesforceRepEnabled
) => {
  return !customerWebViewEnabled && isSalesforceRepEnabled;
};
const RothConversionSteps = ({ actions }) => {
  const dispatch = useDispatch();
  const { openModal, skipRothAllocateToStep, submitRothConversion } =
    moduleActions;

  const currentStep = useSelector((state) =>
    state.progress ? state.progress.currentStep : 0
  );
  const { handleSelectFund, handleFundAllocations } = useSelectedFunds();
  const rothConversionTaxWitholdingStep =
    'roth-conversion-tax-withholding-step';
  const rothConversionSelectBeneficiariesStep =
    'roth-conversion-select-beneficiaries-step';
  const rothConversionTermsConditionsStep =
    'roth-conversion-terms-conditions-step';

  // RMD check
  const isRMDEligible = useSelector(getIsRMDEligible);
  const [isRepSelected, setIsRepSelected] = useState(false);

  //Step 1 next disable
  const meetMinMaxStandard = useSelector(getMinMaxFromFundsCheck);
  const nextDisableStep1 = !meetMinMaxStandard;

  //Step 2 next disable
  const selectedTaxOption = useSelector(getRothConversionTaxOption);
  const taxPercentage = useSelector(getRothConversionTaxPercentage);
  const taxPercentageCheck = useSelector(getRothConversionTaxPercentageCheck);

  const nextDisableStep3 =
    selectedTaxOption === ROTH_CONVERSION_TAX_OPTIONS.YES_WITHHOLD ||
    selectedTaxOption ||
    taxPercentage;
  const percentageError =
    !taxPercentageCheck &&
    selectedTaxOption === ROTH_CONVERSION_TAX_OPTIONS.YES_WITHHOLD;
  const eligibleMandatoryStates = useSelector(getMandatoryWithholdingStates);
  const userState = useSelector(getUserState);
  const showWitholdingWarning = eligibleMandatoryStates?.some(
    (state) => state === userState
  );
  //Step 3 next disable
  const nextDisableStep4 = !useSelector(getValidateUserRothBeneficiaries);

  //Step 4 next disable
  const userAgrees = useSelector(getRothConversionTermsConditions);
  const nextDisableStep5 = !userAgrees;

  const isIFrame = useSelector(isEmbeddedInIFrame);
  const customerWebViewEnabled = useSelector(isCustomerWebViewEnabled);
  const isSalesforceRepEnabled = useSelector(isSalesforceRep);
  const rothIRAFunds = useSelector(getRothIraFunds);

  // state of rothconversion reducer (data used for creating submission payload)
  const rothConversionTransaction = useSelector(
    (state) => state.rothConversionTransaction
  );

  // track final submission
  const executeRothConversion = useSelector(getRothConversionExecuteState);

  function onClickScroll(step, element) {
    dispatch(moduleActions.setProgress(step));
    scrollElementIntoFullView(element, 15);
  }

  // on start up, showRMD alert modal if user is RMD eligible
  // will require RMD data to finish loading
  useEffect(() => {
    if (isRMDEligible) {
      dispatch(openModal(REQUIRED_MINIMUM_DISTRIBUTION_MODAL));
    }
  }, [isRMDEligible, openModal, dispatch]);

  const handleSubmitTradeDate = () => {
    const {
      convertFromFunds,
      allocateToFunds,
      taxPercentage,
      beneficiaries,
      beneficiaryOption,
      userAddedBeneficiaries,
    } = rothConversionTransaction;
    const payload = createRothConversionTransaction({
      convertFromFunds,
      allocateToFunds,
      taxPercentage,
      beneficiaries,
      beneficiaryOption,
      userAddedBeneficiaries,
    });
    Promise.all([dispatch(getRothConversionTradeDates(payload))]).then(() => {
      onClickScroll(5, 'roth-conversion-submit-step');
    });
  };

  return (
    <form onSubmit={(e) => e.preventDefault()} className="animated fadeInUp">
      <If
        test={
          !(
            isIFrame ||
            isNotCustViewAndSalesForceRep(
              customerWebViewEnabled,
              isSalesforceRepEnabled
            )
          )
        }
      >
        <AppTitle
          title="Roth IRA Conversion"
          subtitle="Convert your T. Rowe Price Traditional or Rollover IRA to a Roth IRA."
        />
      </If>
      <ProgressStep
        title={
          <span>
            <span className="exchange-steps-title-bold"></span>Select IRA assets
            to convert to Roth IRA
          </span>
        }
        className="roth-conversion-from-step step1"
        clickable={currentStep > 1}
        step={1}
        active={currentStep === 1}
        complete={currentStep > 1}
        OnClickHeader={() => {
          currentStep > 1 && onClickScroll(1, 'roth-conversion-from-step');
        }}
        expanded
        nextButtonLabel={
          <span>
            <b>NEXT: </b>Tax Withholdings
          </span>
        }
        nextButtonDisabled={nextDisableStep1}
        onNextClick={() => {
          dispatch(skipRothAllocateToStep(rothIRAFunds));
          dispatch(
            openModal(IRA_WITHHOLDING_WARNING_MODAL, {
              showPageIraTable: true,
              onConfirm: () =>
                onClickScroll(2, rothConversionTaxWitholdingStep),
            })
          );
        }}
        showCancelLink={
          !isNotCustViewAndSalesForceRep(
            customerWebViewEnabled,
            isSalesforceRepEnabled
          )
        }
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
      >
        {currentStep === 1 ? (
          <RothConversionFromForm
            step={1}
            onSelectFund={handleSelectFund}
            handleFundAllocations={handleFundAllocations}
            actions={actions}
            customerWebViewEnabled={customerWebViewEnabled}
            isSalesforceRep={isSalesforceRep}
          />
        ) : (
          <RothConversionSummary
            step={1}
            onEdit={() => {
              currentStep > 1 && onClickScroll(1, 'roth-conversion-from-step');
            }}
          />
        )}
      </ProgressStep>
      <ProgressStep
        title={
          <span>
            <span className="exchange-steps-title-bold"></span>Elect federal tax
            withholding
          </span>
        }
        className={`${rothConversionTaxWitholdingStep} step3`}
        clickable={currentStep > 2}
        step={2}
        checkbox={
          currentStep === 2 &&
          isSalesforceRepEnabled &&
          !customerWebViewEnabled ? (
            <div className="sell-fund-availiblity-check-con">
              {' '}
              <CheckBox
                checked={isRepSelected}
                inline={true}
                onChange={(e) => setIsRepSelected(e)}
              />
              <span className="sell-fund-availiblity-check-text">
                I have read the current{' '}
                <a
                  href="https://troweprice.egain.cloud/kb/retail/content/202200000002595/IRA-Redemption-Disclosures"
                  rel="noreferrer"
                  target="_blank"
                >
                  Federal Withholding Disclosures
                </a>
                . Note: Disclosure must be read whether federal withholding
                elected or not
              </span>
            </div>
          ) : (
            <></>
          )
        }
        active={currentStep === 2}
        complete={currentStep > 2}
        OnClickHeader={() => {
          currentStep > 2 && onClickScroll(2, rothConversionTaxWitholdingStep);
        }}
        expanded
        nextButtonClassName="e2e-step3-verify-submit"
        nextButtonLabel={
          <span>
            <b>NEXT:</b> Beneficiaries
          </span>
        }
        nextButtonDisabled={
          percentageError ||
          !nextDisableStep3 ||
          (!isRepSelected && isSalesforceRepEnabled && !customerWebViewEnabled)
        }
        nextButtonLoading={false}
        onNextClick={() => {
          if (taxPercentage >= 81 && showWitholdingWarning) {
            dispatch(openModal(FEDERAL_INCOME_TAX_WARNING));
          }
          onClickScroll(3, rothConversionSelectBeneficiariesStep);
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRepEnabled)}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
      >
        {currentStep === 2 ? (
          <RothConversionTaxWithholdingForm />
        ) : (
          <RothConversionSummary
            step={2}
            onEdit={() => {
              currentStep > 2 &&
                onClickScroll(2, rothConversionTaxWitholdingStep);
            }}
          />
        )}
      </ProgressStep>
      <ProgressStep
        title={
          <span>
            <span className="exchange-steps-title-bold"></span>Select
            Beneficiaries
          </span>
        }
        className={`${rothConversionSelectBeneficiariesStep} step4`}
        clickable={currentStep > 3}
        step={3}
        active={currentStep === 3}
        complete={currentStep > 3}
        OnClickHeader={() => {
          currentStep > 3 &&
            onClickScroll(3, rothConversionSelectBeneficiariesStep);
        }}
        expanded
        nextButtonClassName="e2e-step4-verify-submit"
        nextButtonLabel={
          <span>
            <b>NEXT:</b> Terms and Conditions
          </span>
        }
        nextButtonDisabled={nextDisableStep4}
        nextButtonLoading={false}
        onNextClick={() => {
          onClickScroll(4, rothConversionTermsConditionsStep);
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRepEnabled)}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
      >
        {currentStep === 3 ? (
          <RothBeneficiaryFrom />
        ) : (
          <RothConversionSummary
            step={3}
            onEdit={() => {
              currentStep > 3 &&
                onClickScroll(3, rothConversionSelectBeneficiariesStep);
            }}
          />
        )}
      </ProgressStep>
      <ProgressStep
        title={
          <span>
            <span className="exchange-steps-title-bold"></span>Terms and
            Conditions
          </span>
        }
        className={`${rothConversionTermsConditionsStep} step5`}
        clickable={currentStep > 4}
        step={4}
        active={currentStep === 4}
        complete={currentStep > 4}
        OnClickHeader={() => {
          currentStep > 4 &&
            onClickScroll(4, rothConversionTermsConditionsStep);
        }}
        expanded
        nextButtonClassName="e2e-step5-verify-submit"
        nextButtonLabel={
          <span>
            <b>NEXT:</b> Review & Submit
          </span>
        }
        nextButtonDisabled={nextDisableStep5}
        nextButtonLoading={
          !!executeRothConversion?.isRothConversionTradeDatesLoading
        }
        onNextClick={() => {
          handleSubmitTradeDate();
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRepEnabled)}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
      >
        {currentStep === 4 ? (
          <div>
            <RothConversionTermsAndConditions />
          </div>
        ) : (
          <RothConversionSummary
            step={4}
            onEdit={() => {
              currentStep > 4 &&
                onClickScroll(4, rothConversionTermsConditionsStep);
            }}
          />
        )}
      </ProgressStep>
      <ProgressStep
        title={
          <span>
            <span className="exchange-steps-title-bold"></span>Verify and submit
            the following transactions
          </span>
        }
        className="roth-conversion-submit-step step6"
        clickable={currentStep > 5}
        step={5}
        active={currentStep === 5}
        complete={currentStep > 5}
        OnClickHeader={() => {
          currentStep > 5 && onClickScroll(5, 'roth-conversion-submit-step');
        }}
        expanded
        nextButtonClassName="e2e-step6-verify-submit"
        nextButtonLabel={
          <span>
            <b>Submit</b>
          </span>
        }
        nextButtonDisabled={
          executeRothConversion.isLoading || customerWebViewEnabled
        }
        nextButtonLoading={executeRothConversion.isLoading}
        onNextClick={() => {
          // execute roth conversion
          dispatch(submitRothConversion(rothConversionTransaction));
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRepEnabled)}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
      >
        {currentStep === 5 && (
          <div>
            <RothConversionReview />
          </div>
        )}
      </ProgressStep>
    </form>
  );
};

const mapStateToProps = (state) => {
  const { rothConversionTransaction } = state;
  return {
    rothConversionTransaction,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: {
    ...bindActionCreators(moduleActions, dispatch),
    setProgress: (step) => {
      scrollElementIntoFullView(`step${step}`);
      dispatch(moduleActions.setProgress(step));
    },
    submitRothConversion: moduleActions.submitRothConversion,
  },
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  RothConversionSteps
);
